<template>
  <portal to="modal-windows">
    <AppDialog
      :show="show"
      :title="$t('levels.reassignElements')"
      :type="DIALOG_TYPES.INFO"
      hide-hero
      @on-close="onClose"
    >
      <div>
        <i18n-t
          v-if="show"
          class="lrm-ConfirmMessage"
          keypath="levels.select_available_element_type"
          scope="global"
          tag="div"
        />

        <div class="lrm-Items">
          <div v-for="conflict in conflictModelValue" :key="conflict.workspaceId">
            <FormFieldNext :label="conflict.conflictName" required>
              <AppSelect
                v-model="conflict.levelId"
                :hidden-items="getHiddenItems(conflict)"
                :is-error="conflict.isError"
                :item-label="OKR_LEVEL_ENTITY_KEYS.NAME"
                :item-value="OKR_LEVEL_ENTITY_KEYS.ID"
                :offset="[0, 0]"
                :options="levelsAsOptions"
                :type="SELECT_TYPES.MODERN"
                @open="clearError(conflict)"
              >
                <template #button-content="{ option }">
                  <OkrTypeFieldOption v-if="option" :option="option" />
                  <template v-else>
                    {{ $t('action.select_element_type') }}
                  </template>
                </template>
                <template #option-label="{ option }">
                  <OkrTypeFieldOption v-if="option" :option="option" />
                </template>
              </AppSelect>
              <AppFieldError
                v-if="conflict.isError"
                :show="conflict.isError"
                class="lrm-Error"
                data-testid="level-field-error"
              >
                {{ $t('field.required') }}
              </AppFieldError>
            </FormFieldNext>
          </div>
        </div>
      </div>
      <template #footer-actions>
        <AppButton :loading="loading" data-auto-testid="dialog-confirm-button" @click="onConfirm">
          {{ $t('action.proceed') }}
        </AppButton>
      </template>
    </AppDialog>
  </portal>
</template>

<script setup>
import { cloneDeep, isEmpty, isNull } from 'lodash'
import { computed, ref, watch } from 'vue'

import LevelApiHandler from '@/api/level'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { SELECT_TYPES } from '@/utils/components-configurations/app-select'
import { OKR_LEVEL_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isKR } from '@/utils/objective-types'
import { getMetricLevelsForAdd, getObjectiveLevelsForCreate } from '@/utils/objectives'

import AppDialog from '@/components/AppDialog'
import AppFieldError from '@/components/form/AppFieldError'
import FormFieldNext from '@/components/form/FormFieldNext'
import OkrTypeFieldOption from '@/components/form/OkrTypeFieldOption'
import AppButton from '@/components/ui/AppButton/AppButton'
import AppSelect from '@/components/ui/AppSelect/AppSelect'

defineOptions({ name: 'LevelReassignModal' })

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },

  conflicts: {
    type: Array,
    default: () => []
  },

  level: {
    type: Object,
    default: () => ({})
  },

  levels: {
    type: Array,
    default: () => []
  }
})

const loading = ref(false)

const validate = () => {
  conflictModelValue.value = conflictModelValue.value.map(item => {
    return {
      ...item,
      isError: isNull(item.levelId)
    }
  })
}

const metricLevels = computed(() => getMetricLevelsForAdd(props.levels))
const objectiveLevels = computed(() => getObjectiveLevelsForCreate(props.levels))

const levelsAsOptions = computed(() => {
  return isKR(props.level) ? metricLevels.value : objectiveLevels.value
})

const getHiddenItems = ({ workspaceId = null } = {}) => {
  const hiddenLevels = levelsAsOptions.value.reduce((acc, level) => {
    const isSameWorkspace = level.workspaces.find(ws => ws.id === workspaceId)

    if (isSameWorkspace) {
      return acc
    }

    return [...acc, level[OKR_LEVEL_ENTITY_KEYS.ID]]
  }, [])

  return [props.level[OKR_LEVEL_ENTITY_KEYS.ID], ...hiddenLevels]
}

const isErrors = computed(() => !isEmpty(conflictModelValue.value.filter(item => item.isError)))

const clearError = conflict => {
  conflict.isError = false
}

const onConfirm = async () => {
  validate()
  if (!isErrors.value) {
    const levelApi = new LevelApiHandler()
    loading.value = true
    try {
      const payload = {
        oldLevelId: props.level.id,
        newLevels: conflictModelValue.value.map(conflict => {
          return {
            workspaceId: conflict.workspaceId,
            levelId: conflict.levelId
          }
        })
      }
      await levelApi.reassignLevels(payload)
      onClose()
      emit('reassigned', props.level)
    } catch (error) {
      handleError({ error })
    } finally {
      loading.value = false
    }
  }
}

const emit = defineEmits({
  'update:show': null,
  reassigned: null
})
const onClose = () => {
  emit('update:show', false)
}

const conflictModelValue = ref([])

watch(
  () => props.show,
  newValue => {
    if (newValue) {
      conflictModelValue.value = cloneDeep(props.conflicts).map(conflict => {
        return {
          levelId: null,
          isError: false,
          workspaceId: conflict.id,
          conflictName: conflict.name
        }
      })
    }
  }
)
</script>

<style lang="scss" scoped>
.lrm-ConfirmMessage {
  margin: 0 0 20px 0;
}

.lrm-Items {
  display: grid;
  gap: 10px;
}

.lrm-Error {
  min-height: 20px;
  display: flex;
  align-items: center;
  // position: absolute;
  // bottom: -20px;
  // left: 0;
}
</style>
